/**
 * 조직 휴가 관리 페이지 (V66.1)
 *
 * 조직관리 > 조직 휴가 관리
 *
 * **핵심:**
 * - 인사관리 > 휴가현황 페이지와 동일한 UI/UX
 * - 차이점: 조회 범위가 "자신의 조직 + 하위 조직 인원"으로 제한 (Backend SSoT)
 * - 읽기 전용 (임포트/수정/동기화 없음)
 *
 * **데이터 소스:**
 * - getOrgLeaveOverview Callable Function (조직 스코프 기반 조회)
 *
 * **V5.18.x 변경:**
 * - 월별 사용량을 Frontend 직접 쿼리(useLeaveMonthlyUsageBatch)에서
 *   Backend CF 응답(monthlyUsageMap)으로 변경 (조직장 권한 문제 해결)
 *
 * **V66.1:**
 * - 대장님 룰 정렬 완전 구현 (FE 조인 방식)
 * - 핵심 수정: useOrgManagementScope로 스코프를 가져와 useEmployees에 전달
 *   → 조직장/팀장 권한으로도 스코프 내 직원 마스터 데이터 조회 가능
 * - row.employeeId로 직원 마스터 데이터 조인하여 titleId, positionId, firstHireDate 보강
 * - 정렬 우선순위: 직책순 → 직위순 → 입사일순 → 이름순
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Users, Filter, Download, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { getFunctions } from '../../firebase/utils';
// V66.1: FE 조인용 훅 (직원 마스터, 직책, 직위, 조직 스코프)
import { useOrgManagementScope } from '../../shared/hooks/useOrgManagementScope';
import { useEmployees } from '../../shared/hooks/useEmployees';
import { useJobTitles } from '../../shared/hooks/useJobTitles';
import { usePositionLevelMap } from '../../shared/hooks/usePositionLevelMap';
import { formatCompOffDaysHours } from '../../shared/utils/formatCompOff';  // V78.1
// V66: 대장님 룰 정렬 유틸
import {
  compareOrgMember,
  OrgMemberSortMaps,
  buildJobTitleMaps,
  buildPositionLevelMaps,
} from '../../shared/utils/sortOrgMembers';

// ===== 타입 정의 =====

/**
 * Backend 응답 타입 (getOrgLeaveOverview)
 */
interface EmployeeLeaveData {
  employeeId: string;
  employeeNumber: string;
  nameKo: string;
  orgUnitId: string | null;
  orgUnitName: string | null;
  companyCode?: string; // V62: 대체휴무 지급 UI 조건용 (CN만 노출)
  status: string;
  balance: {
    annual?: {
      granted: number;
      used: number;
      remaining: number;
      carryOver: number;
      negative: number;
      preUsed?: number;
      validFrom?: string;
      validUntil?: string;
      periodStart?: string;
      periodEnd?: string;
      monthlyUsage?: Record<number, number>;
    };
    compensatory?: {
      granted: number;
      used: number;
      remaining: number;
    };
    personalHealing?: {
      q1Used: boolean;
      q2Used: boolean;
      q3Used: boolean;
      q4Used: boolean;
      quarterlyUsage?: Record<number, number>;
    };
  } | null;
}

/**
 * V5.18.x: 월별 사용량 집계 결과 (Backend SSoT)
 */
interface MonthlyUsageResult {
  monthlyUsage: Record<number, number>;
  importTotal: number;
  normalTotal: number;
  grandTotal: number;
}

/**
 * V5.19: 요청 타입
 * V201.1: debug 플래그 추가
 */
interface GetOrgLeaveOverviewRequest {
  year?: number;
  /** V201.1: 디버그 모드 (DEV 환경에서만 true) */
  debug?: boolean;
}

// V201.1: DEV 환경 여부 (debug 플래그 결정용)
const IS_DEV = process.env.NODE_ENV === 'development';

/**
 * V66: 대체휴무 컬럼 전용 시간표기 (1일=8시간)
 * V69 (2026-06-10): Math.round → Math.floor — 1시간 미만 round-off 잔재 흡수 차단.
 * - 입력: days (예: 0.5, 1, 1.25)
 * - 출력: "nH" (예: "4H", "8H", "10H")
 */
function formatHours(days?: number): string {
  const d = Number(days ?? 0);
  const hours = Math.floor(d * 8);
  return `${hours}H`;
}

interface GetOrgLeaveOverviewResponse {
  success: boolean;
  data: {
    employees: EmployeeLeaveData[];
    totalCount: number;
    scopeMode: string;
    scopeOrgUnitIds: string[];
    /** V5.18.x: 직원별 월별 사용량 (leaveHistories SSoT) */
    monthlyUsageMap: Record<string, MonthlyUsageResult>;
    /** V5.19: 조회 연도 */
    year: number;
  };
  message?: string;
}

// ===== 컴포넌트 =====

export function OrgLeaveManagementPage() {
  // V5.19: 연도 선택 (SSoT)
  const currentYear = useMemo(() => new Date().getFullYear(), []);
  const [selectedYear, setSelectedYear] = useState<number>(currentYear);

  // 연도 옵션 (현재연도-5 ~ 현재연도)
  const yearOptions = useMemo(() => {
    const options: number[] = [];
    for (let y = currentYear; y >= currentYear - 5; y--) {
      options.push(y);
    }
    return options;
  }, [currentYear]);

  // 데이터 상태
  const [employees, setEmployees] = useState<EmployeeLeaveData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [scopeMode, setScopeMode] = useState<string>('');
  // V5.18.x: 월별 사용량 (Backend SSoT)
  const [monthlyUsageMap, setMonthlyUsageMap] = useState<Record<string, MonthlyUsageResult>>({});

  // ============================================
  // V66.1: FE 조인용 훅 - 조직 스코프 기반 직원 마스터 데이터
  // 핵심: useOrgManagementScope로 스코프를 가져와 useEmployees에 전달
  //       → 조직장/팀장 권한으로도 스코프 내 직원 조회 가능
  // V201: 스코프 준비 전에는 useEmployees 호출 대기
  //       → 스코프 로딩 중 getEmployees 권한 에러 방지
  // ============================================
  const { scopeOrgUnitIds, scopeMode: orgScopeMode } = useOrgManagementScope();
  // V201: 스코프가 준비된 후에만 useEmployees 호출 (빈 객체 전달 시 대기 모드)
  const scopeReady = scopeOrgUnitIds.length > 0 && !!orgScopeMode;
  const { employees: masterEmployees } = useEmployees(
    scopeReady
      ? {
          orgUnitIds: scopeOrgUnitIds,
          scopeMode: orgScopeMode,
          status: 'active_or_on_leave',
        }
      : {} // 빈 객체 → useEmployees에서 대기 (V201)
  );
  const { jobTitles } = useJobTitles({ status: 'active' });
  const { positionLevels } = usePositionLevelMap();

  // V61: 대체휴무 지급 모달 상태
  const [compOffTarget, setCompOffTarget] = useState<EmployeeLeaveData | null>(null);
  const [compOffDays, setCompOffDays] = useState<string>('');
  const [compOffReason, setCompOffReason] = useState<string>('');
  const [compOffLoading, setCompOffLoading] = useState(false);
  const [compOffError, setCompOffError] = useState<string | null>(null);
  // V78: 지급 단위 전환 (일수 / 시간)
  const [compOffUnit, setCompOffUnit] = useState<'days' | 'hours'>('days');
  const [compOffHours, setCompOffHours] = useState<string>('');

  // V64: 대체휴무 지급 히스토리 상태
  interface CompOffGrantHistoryItem {
    id: string;
    employeeId: string;
    days: number;
    inputType: 'days' | 'hours';
    inputValue: number;
    reason: string;
    grantedBy: string;
    grantedByName: string;
    grantedAt: string;
    status: 'active' | 'cancelled';
  }
  const [grantHistories, setGrantHistories] = useState<CompOffGrantHistoryItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // V70: 대체휴무 지급 취소 모달 상태
  const [cancelTargetGrant, setCancelTargetGrant] = useState<CompOffGrantHistoryItem | null>(null);
  const [cancelReason, setCancelReason] = useState<string>('');
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);

  // 필터 상태
  const [searchText, setSearchText] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');

  // 데이터 로드
  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const functions = getFunctions();
      const getOrgLeaveOverviewFn = httpsCallable<GetOrgLeaveOverviewRequest, GetOrgLeaveOverviewResponse>(
        functions,
        'getOrgLeaveOverview'
      );

      // V5.19: year 파라미터 전달
      // V201.1: debug 플래그 추가 (DEV 환경에서만 true)
      const result = await getOrgLeaveOverviewFn({ year: selectedYear, debug: IS_DEV });

      if (result.data.success) {
        setEmployees(result.data.data.employees);
        setScopeMode(result.data.data.scopeMode);
        // V5.18.x: 월별 사용량 (Backend SSoT)
        setMonthlyUsageMap(result.data.data.monthlyUsageMap || {});
      } else {
        throw new Error(result.data.message || '데이터 조회 실패');
      }
    } catch (err: any) {
      // V201.3: 에러 상세 정보 콘솔 출력 - JSON.stringify로 복사 가능하게
      console.error('[OrgLeaveManagementPage] 조회 실패:', err);
      if (err.details) {
        // V201.3: Object가 아닌 복사 가능한 JSON 텍스트로 출력
        console.error(
          '[OrgLeaveManagementPage] 에러 상세 (복사용 JSON):',
          JSON.stringify(err.details, null, 2)
        );
      }

      // V201.1: step 정보가 있으면 에러 메시지에 포함
      const stepInfo = err.details?.step ? ` [step: ${err.details.step}]` : '';

      if (err.code === 'functions/permission-denied') {
        setError(`조직 휴가 관리에 접근할 권한이 없습니다.${stepInfo}`);
      } else if (err.code === 'functions/unauthenticated') {
        setError('로그인이 필요합니다.');
      } else if (err.code === 'functions/invalid-argument') {
        setError(err.message || '잘못된 연도입니다.');
      } else if (err.code === 'functions/internal') {
        // V201.1: internal 에러는 step 정보 표시
        setError(`서버 오류가 발생했습니다.${stepInfo} (콘솔에서 상세 확인)`);
      } else {
        setError(err.message || `데이터 조회 중 오류가 발생했습니다.${stepInfo}`);
      }
    } finally {
      setIsLoading(false);
    }
  }, [selectedYear]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // V64: 대체휴무 지급 히스토리 조회
  const fetchGrantHistory = useCallback(async (employeeId: string) => {
    try {
      setHistoryLoading(true);
      const functions = getFunctions();
      const fn = httpsCallable<
        { employeeId: string; limit?: number },
        { success: boolean; histories: CompOffGrantHistoryItem[] }
      >(functions, 'getCompOffGrantHistory');
      const result = await fn({ employeeId, limit: 50 });
      if (result.data.success) {
        setGrantHistories(result.data.histories || []);
      } else {
        setGrantHistories([]);
      }
    } catch (err) {
      console.error('[V64] 히스토리 조회 실패:', err);
      setGrantHistories([]);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  // V64: 사이드바 열릴 때 히스토리 자동 fetch
  useEffect(() => {
    if (compOffTarget) {
      fetchGrantHistory(compOffTarget.employeeId);
    } else {
      setGrantHistories([]);
    }
  }, [compOffTarget, fetchGrantHistory]);

  // V61/V78: 대체휴무 지급 처리 (일수/시간 선택)
  const handleGrantCompOff = useCallback(async () => {
    if (!compOffTarget) return;
    if (!compOffReason.trim()) {
      setCompOffError('사유를 입력해주세요.');
      return;
    }

    // V78: 단위에 따라 payload 구성
    let payload: { employeeId: string; reason: string; days?: number; hours?: number };

    if (compOffUnit === 'days') {
      const days = parseFloat(compOffDays);
      if (isNaN(days) || days <= 0) {
        setCompOffError('0보다 큰 숫자를 입력해주세요.');
        return;
      }
      // V69 (2026-06-10): 0.5일 단위 정책 (1일=8시간 기준). 0.1/0.3/0.7 등 차단.
      const rounded = Math.round(days * 2) / 2;
      if (Math.abs(days - rounded) > 0.001) {
        setCompOffError('0.5일 단위만 입력 가능합니다. (예: 0.5, 1, 1.5)');
        return;
      }
      payload = {
        employeeId: compOffTarget.employeeId,
        reason: compOffReason.trim(),
        days: rounded,
      };
    } else {
      const hours = parseInt(compOffHours, 10);
      if (isNaN(hours) || hours < 1) {
        setCompOffError('1 이상의 정수(시간)를 입력해주세요.');
        return;
      }
      if (!Number.isInteger(hours)) {
        setCompOffError('시간은 정수만 입력 가능합니다.');
        return;
      }
      payload = {
        employeeId: compOffTarget.employeeId,
        reason: compOffReason.trim(),
        hours,
      };
    }

    try {
      setCompOffLoading(true);
      setCompOffError(null);

      const functions = getFunctions();
      const grantFn = httpsCallable<
        typeof payload,
        { success: boolean; message?: string; newGranted?: number; newRemaining?: number }
      >(functions, 'grantCompensatoryLeave');

      const result = await grantFn(payload);

      if (result.data.success) {
        // V78.1: 입력 단위 그대로 표시 (서버 환산 결과 newGranted는 참고용)
        const unitLabel = compOffUnit === 'days'
          ? formatCompOffDaysHours(payload.days)
          : `${payload.hours}시간`;
        alert(`${compOffTarget.nameKo}님에게 대체휴무 ${unitLabel}이(가) 지급되었습니다.`);
        // V64: 사이드바 유지, 입력값만 초기화 + 히스토리 즉시 갱신
        setCompOffDays('');
        setCompOffHours('');
        setCompOffUnit('days');
        setCompOffReason('');
        fetchData();
        fetchGrantHistory(compOffTarget.employeeId);
      }
    } catch (err: any) {
      setCompOffError(err.message || '대체휴무 지급 중 오류가 발생했습니다.');
    } finally {
      setCompOffLoading(false);
    }
  }, [compOffTarget, compOffDays, compOffHours, compOffUnit, compOffReason, fetchData, fetchGrantHistory]);

  // V70: 대체휴무 지급 취소 핸들러
  const handleConfirmCancelGrant = useCallback(async () => {
    if (!cancelTargetGrant) return;
    const reason = cancelReason.trim();
    if (!reason) {
      setCancelError('취소 사유를 입력해주세요.');
      return;
    }
    try {
      setCancelLoading(true);
      setCancelError(null);
      const functions = getFunctions();
      const cancelFn = httpsCallable<
        { grantId: string; cancelReason: string },
        { success: boolean; message?: string; cancelledDays?: number; newGranted?: number; newRemaining?: number }
      >(functions, 'cancelCompensatoryGrant');
      const result = await cancelFn({ grantId: cancelTargetGrant.id, cancelReason: reason });
      if (result.data.success) {
        alert(result.data.message || '대체휴무 지급이 취소되었습니다.');
        setCancelTargetGrant(null);
        setCancelReason('');
        fetchData();
        if (compOffTarget) fetchGrantHistory(compOffTarget.employeeId);
      }
    } catch (err: any) {
      setCancelError(err?.message || '대체휴무 지급 취소 중 오류가 발생했습니다.');
    } finally {
      setCancelLoading(false);
    }
  }, [cancelTargetGrant, cancelReason, fetchData, fetchGrantHistory, compOffTarget]);

  // ============================================
  // V66: FE 조인 기반 대장님 룰 정렬
  // 정렬 우선순위: 1) 직책순 2) 직위순 3) 입사일순 4) 이름순
  // ============================================

  // V66: 직원 마스터 데이터 맵 (employeeId → { titleId, positionId, firstHireDate })
  const employeeMasterMap = useMemo(() => {
    const map: Record<string, { titleId?: string; positionId?: string; firstHireDate?: string; currentEmploymentTypeCode?: string }> = {};
    masterEmployees.forEach((emp) => {
      map[emp.id] = {
        titleId: emp.titleId,
        positionId: emp.positionId,
        firstHireDate: emp.firstHireDate,
        currentEmploymentTypeCode: emp.currentEmploymentTypeCode, // V62: CN 인턴 적용기간 표시용
      };
    });
    return map;
  }, [masterEmployees]);

  // V66: 정렬 맵 구성 (직책 order, 직위 rankWeight)
  const sortMaps: OrgMemberSortMaps = useMemo(() => {
    const { titleIdToOrderMap, titleNameToOrderMap } = buildJobTitleMaps(jobTitles);
    const { positionIdToRankWeightMap, positionNameToRankWeightMap } = buildPositionLevelMaps(positionLevels);
    return {
      titleIdToOrderMap,
      titleNameToOrderMap,
      positionIdToRankWeightMap,
      positionNameToRankWeightMap,
    };
  }, [jobTitles, positionLevels]);

  // V66.1: FE 조인하여 정렬용 필드 보강 후 정렬
  const sortedEmployees = useMemo(() => {
    // V66.1 DEBUG: 조인 성공률 확인
    const hitCount = employees.filter((e) => employeeMasterMap[e.employeeId]).length;
    // employeeId로 마스터 데이터 조인하여 titleId, positionId, firstHireDate, currentEmploymentTypeCode 보강
    const enriched = employees.map((emp) => {
      const master = employeeMasterMap[emp.employeeId];
      return {
        ...emp,
        titleId: master?.titleId,
        positionId: master?.positionId,
        firstHireDate: master?.firstHireDate,
        currentEmploymentTypeCode: master?.currentEmploymentTypeCode, // V62: CN 인턴 적용기간 표시용
      };
    });
    // 대장님 룰: 직책순 → 직위순 → 입사일순 → 이름순
    return enriched.sort((a, b) => compareOrgMember(a, b, sortMaps));
  }, [employees, employeeMasterMap, sortMaps]);

  // 필터링 (V66: sortedEmployees 기반)
  const filteredData = useMemo(() => {
    return sortedEmployees.filter((emp) => {
      const matchesSearch =
        !searchText ||
        (emp.nameKo || '').toLowerCase().includes(searchText.toLowerCase()) ||
        (emp.orgUnitName || '').toLowerCase().includes(searchText.toLowerCase()) ||
        (emp.employeeNumber || '').toLowerCase().includes(searchText.toLowerCase());

      // V66: 'leave' 옵션은 'on_leave' / 'leave' 두 값 모두 매칭 (BE 가 양쪽 허용 — legacy + 신규 정책 일관)
      let matchesStatus: boolean;
      if (filterStatus === 'all') {
        matchesStatus = true;
      } else if (filterStatus === 'leave') {
        matchesStatus = emp.status === 'on_leave' || emp.status === 'leave';
      } else {
        matchesStatus = emp.status === filterStatus;
      }

      return matchesSearch && matchesStatus;
    });
  }, [sortedEmployees, searchText, filterStatus]);

  // 통계
  const stats = {
    totalEmployees: employees.length,
    scopeMode: scopeMode === 'ALL' ? '전체' : scopeMode === 'SUBTREE' ? '하위 조직' : '본인 조직',
  };

  // 에러 상태
  if (error) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <p style={{ color: 'var(--color-red-600)', marginBottom: '16px' }}>{error}</p>
        <button
          onClick={fetchData}
          style={{
            padding: '8px 16px',
            background: 'var(--color-blue-600)',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
          }}
        >
          다시 시도
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* 페이지 제목 */}
      <h1
        style={{
          fontSize: '24px',
          fontWeight: 'bold',
          color: 'var(--color-gray-800)',
          marginBottom: '8px',
        }}
      >
        조직 휴가 현황
      </h1>
      <p
        style={{
          fontSize: '13px',
          color: 'var(--color-gray-500)',
          marginBottom: '6px',
        }}
      >
        {selectedYear}년 {stats.scopeMode} 범위 재직/휴직자 기준 (읽기 전용)
      </p>
      {/* V68: 페이지 가이드 안내 */}
      <div style={{ marginBottom: '24px', color: '#94a3b8', fontSize: '13px', lineHeight: 1.6 }}>
        <p style={{ margin: 0 }}>· 직원 이름을 클릭하면 대체 휴무를 지급할 수 있습니다.</p>
      </div>

      {/* 전체 통계 */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '16px',
          marginBottom: '24px',
        }}
      >
        <div
          style={{
            background: 'white',
            padding: '20px',
            borderRadius: '8px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          }}
        >
          <div
            style={{ fontSize: '14px', color: 'var(--color-gray-600)', marginBottom: '8px' }}
          >
            조직 인원
          </div>
          <div style={{ fontSize: '28px', fontWeight: 'bold', color: 'var(--color-blue-600)' }}>
            {stats.totalEmployees}명
          </div>
        </div>
      </div>

      {/* 필터 및 검색 */}
      <div
        style={{
          background: 'white',
          padding: '16px',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          marginBottom: '16px',
          display: 'flex',
          gap: '16px',
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        {/* V5.19: 연도 선택 UI */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <button
            onClick={() => setSelectedYear((prev) => Math.max(prev - 1, currentYear - 5))}
            disabled={selectedYear <= currentYear - 5 || isLoading}
            style={{
              padding: '6px',
              background: selectedYear <= currentYear - 5 ? 'var(--color-gray-200)' : 'var(--color-gray-100)',
              border: '1px solid var(--color-gray-300)',
              borderRadius: '4px',
              cursor: selectedYear <= currentYear - 5 ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
            }}
            title="이전 연도"
          >
            <ChevronLeft size={16} />
          </button>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            disabled={isLoading}
            style={{
              padding: '8px 12px',
              border: '1px solid var(--color-gray-300)',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: 'bold',
              minWidth: '100px',
              textAlign: 'center',
            }}
          >
            {yearOptions.map((y) => (
              <option key={y} value={y}>
                {y}년
              </option>
            ))}
          </select>
          <button
            onClick={() => setSelectedYear((prev) => Math.min(prev + 1, currentYear))}
            disabled={selectedYear >= currentYear || isLoading}
            style={{
              padding: '6px',
              background: selectedYear >= currentYear ? 'var(--color-gray-200)' : 'var(--color-gray-100)',
              border: '1px solid var(--color-gray-300)',
              borderRadius: '4px',
              cursor: selectedYear >= currentYear ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
            }}
            title="다음 연도"
          >
            <ChevronRight size={16} />
          </button>
        </div>

        <div style={{ width: '1px', height: '24px', background: 'var(--color-gray-300)' }} />

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Filter size={16} style={{ color: 'var(--color-gray-600)' }} />
          <input
            type="text"
            placeholder="이름 또는 조직 검색..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{
              padding: '8px 12px',
              border: '1px solid var(--color-gray-300)',
              borderRadius: '6px',
              fontSize: '14px',
              width: '300px',
            }}
          />
        </div>

        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          style={{
            padding: '8px 12px',
            border: '1px solid var(--color-gray-300)',
            borderRadius: '6px',
            fontSize: '14px',
          }}
        >
          <option value="all">전체 상태</option>
          <option value="active">재직</option>
          <option value="leave">휴직</option>
        </select>

        <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px', flexShrink: 0 }}>
          <button
            onClick={() => {
              alert('엑셀 다운로드 기능은 추후 구현 예정입니다.');
            }}
            style={{
              padding: '8px 16px',
              background: 'var(--color-green-600)',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <Download size={16} />
            엑셀 다운로드
          </button>
        </div>
      </div>

      {/* 직원 휴가 현황 테이블 */}
      <div
        style={{
          background: 'white',
          padding: '20px',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        }}
      >
        <h2
          style={{
            fontSize: '18px',
            fontWeight: 'bold',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <Users size={20} />
          직원별 휴가 현황
        </h2>

        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: 'var(--color-gray-500)' }}>
            로딩 중...
          </div>
        ) : filteredData.length > 0 ? (
          <div style={{ overflowX: 'auto', width: '100%' }}>
            <table style={{ borderCollapse: 'collapse', border: '1px solid var(--color-gray-100)', width: '100%', tableLayout: 'fixed' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--color-gray-200)' }}>
                  {/* V5.18: 수정 버튼 제거 (읽기 전용) */}
                  <th style={{ padding: '8px 4px', textAlign: 'center', fontSize: '13px', fontWeight: '600', whiteSpace: 'nowrap', width: '5%', minWidth: '100px', borderRight: '1px solid var(--color-gray-100)' }}>사번</th>
                  <th style={{ padding: '8px 4px', textAlign: 'center', fontSize: '13px', fontWeight: '600', whiteSpace: 'nowrap', width: '5%', minWidth: '86px', borderRight: '1px solid var(--color-gray-100)' }}>이름</th>
                  <th style={{ padding: '8px 4px', textAlign: 'center', fontSize: '13px', fontWeight: '600', whiteSpace: 'nowrap', width: '9%', minWidth: '161px', borderRight: '1px solid var(--color-gray-100)' }}>조직</th>
                  <th style={{ padding: '8px 2px', textAlign: 'center', fontSize: '13px', fontWeight: '600', whiteSpace: 'nowrap', width: '4%', minWidth: '76px', borderRight: '1px solid var(--color-gray-100)' }}>발생</th>
                  <th style={{ padding: '8px 2px', textAlign: 'center', fontSize: '13px', fontWeight: '600', whiteSpace: 'nowrap', width: '4%', minWidth: '76px', borderRight: '1px solid var(--color-gray-100)' }}>사용</th>
                  <th style={{ padding: '8px 2px', textAlign: 'center', fontSize: '13px', fontWeight: '600', whiteSpace: 'nowrap', width: '4.5%', minWidth: '86px', borderRight: '1px solid var(--color-gray-100)' }}>선사용</th>
                  <th style={{ padding: '8px 2px', textAlign: 'center', fontSize: '13px', fontWeight: '600', whiteSpace: 'nowrap', width: '4%', minWidth: '76px', borderRight: '1px solid var(--color-gray-100)' }}>잔여</th>
                  <th style={{ padding: '8px 2px', textAlign: 'center', fontSize: '13px', fontWeight: '600', whiteSpace: 'nowrap', width: '7.5%', minWidth: '141px', borderRight: '1px solid var(--color-gray-100)' }}>적용시작</th>
                  <th style={{ padding: '8px 2px', textAlign: 'center', fontSize: '13px', fontWeight: '600', whiteSpace: 'nowrap', width: '7.5%', minWidth: '141px', borderRight: '1px solid var(--color-gray-100)' }}>적용종료</th>
                  {/* 월별 사용량 1~12월 */}
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((m) => (
                    <th key={`month-${m}`} style={{ padding: '8px 2px', textAlign: 'center', fontSize: '13px', fontWeight: '600', whiteSpace: 'nowrap', width: '3.5%', minWidth: '60px', borderRight: '1px solid var(--color-gray-100)' }}>{m}월</th>
                  ))}
                  {/* 퍼힐 1~4분기 */}
                  <th style={{ padding: '8px 2px', textAlign: 'center', fontSize: '13px', fontWeight: '600', whiteSpace: 'nowrap', width: '5%', minWidth: '80px', borderRight: '1px solid var(--color-gray-100)' }}>퍼힐1Q</th>
                  <th style={{ padding: '8px 2px', textAlign: 'center', fontSize: '13px', fontWeight: '600', whiteSpace: 'nowrap', width: '5%', minWidth: '80px', borderRight: '1px solid var(--color-gray-100)' }}>퍼힐2Q</th>
                  <th style={{ padding: '8px 2px', textAlign: 'center', fontSize: '13px', fontWeight: '600', whiteSpace: 'nowrap', width: '5%', minWidth: '80px', borderRight: '1px solid var(--color-gray-100)' }}>퍼힐3Q</th>
                  <th style={{ padding: '8px 2px', textAlign: 'center', fontSize: '13px', fontWeight: '600', whiteSpace: 'nowrap', width: '5%', minWidth: '80px', borderRight: '1px solid var(--color-gray-100)' }}>퍼힐4Q</th>
                  {/* V66: 대체휴무 발생/사용/잔여 — leaveBalances.compensatory SSoT (BE getOrgLeaveOverview 응답 포함) */}
                  <th style={{ padding: '8px 2px', textAlign: 'center', fontSize: '13px', fontWeight: '600', whiteSpace: 'nowrap', width: '5%', minWidth: '80px', borderRight: '1px solid var(--color-gray-100)' }}>대휴발생</th>
                  <th style={{ padding: '8px 2px', textAlign: 'center', fontSize: '13px', fontWeight: '600', whiteSpace: 'nowrap', width: '5%', minWidth: '80px', borderRight: '1px solid var(--color-gray-100)' }}>대휴사용</th>
                  <th style={{ padding: '8px 2px', textAlign: 'center', fontSize: '13px', fontWeight: '600', whiteSpace: 'nowrap', width: '5%', minWidth: '80px' }}>대휴잔여</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.map((emp) => {
                  const balance = emp.balance;
                  // V75.5: 월별 사용량 — balance(DB) 우선, histories fallback
                  // V65-FIX(2026-04-22): 우선순위 반전 (HrLeaveOverviewPage와 동일 정책)
                  // histories(useLeaveMonthlyUsageBatch)는 source='import' 레코드를 월별 분해 못함 →
                  // import 포함 월이 누락됨. BE가 재계산한 DB(leaveBalances.annual.monthlyUsage) 우선.
                  const usageFromHistories = monthlyUsageMap[emp.employeeId]?.monthlyUsage;
                  const balanceMonthlyUsage = balance?.annual?.monthlyUsage || {};
                  const monthlyUsage: Record<number | string, number> = {};
                  for (let m = 1; m <= 12; m++) {
                    if ((balanceMonthlyUsage as Record<number, number>)[m] !== undefined) {
                      monthlyUsage[m] = (balanceMonthlyUsage as Record<number, number>)[m];
                    } else if (usageFromHistories && usageFromHistories[m] !== undefined) {
                      monthlyUsage[m] = usageFromHistories[m];
                    }
                  }
                  // 퍼힐 분기별
                  const q1 = balance?.personalHealing?.q1Used;
                  const q2 = balance?.personalHealing?.q2Used;
                  const q3 = balance?.personalHealing?.q3Used;
                  const q4 = balance?.personalHealing?.q4Used;

                  return (
                    <tr key={emp.employeeId} style={{ borderBottom: '1px solid var(--color-gray-100)' }}>
                      {/* 사번 */}
                      <td style={{ padding: '8px 4px', textAlign: 'center', fontSize: '13px', width: '5%', minWidth: '100px', borderRight: '1px solid var(--color-gray-100)' }}>
                        {emp.employeeNumber || '-'}
                      </td>
                      {/* 이름 — V61: 클릭 시 대체휴무 지급 모달 / V280.1: KR/CN 모두 클릭 가능 */}
                      <td style={{ padding: '8px 4px', textAlign: 'center', fontSize: '13px', borderRight: '1px solid var(--color-gray-100)' }}>
                        <span
                          onClick={() => { setCompOffTarget(emp); setCompOffDays(''); setCompOffReason(''); setCompOffError(null); }}
                          style={{
                            cursor: 'pointer',
                            // V66: 휴직 직원 — 옅은 주황 (#c2410c)
                            color: (emp.status === 'on_leave' || emp.status === 'leave') ? '#c2410c' : 'inherit',
                            textDecoration: 'underline',
                          }}
                        >
                          {emp.nameKo || '-'}
                        </span>
                        {/* V66: 휴직중 배지 */}
                        {(emp.status === 'on_leave' || emp.status === 'leave') && (
                          <span style={{
                            marginLeft: 4, padding: '1px 6px', borderRadius: 8,
                            background: '#fff7ed', color: '#c2410c', border: '1px solid #fed7aa',
                            fontSize: 10, fontWeight: 600,
                          }}>
                            휴직중
                          </span>
                        )}
                      </td>
                      {/* 조직 */}
                      <td style={{ padding: '8px 4px', textAlign: 'center', fontSize: '13px', borderRight: '1px solid var(--color-gray-100)' }}>
                        {emp.orgUnitName || '-'}
                      </td>
                      {/* 발생 */}
                      <td style={{ padding: '8px 2px', textAlign: 'center', fontSize: '13px', borderRight: '1px solid var(--color-gray-100)' }}>
                        {balance ? (balance.annual?.granted ?? 0) : '-'}
                      </td>
                      {/* 사용 */}
                      <td style={{ padding: '8px 2px', textAlign: 'center', fontSize: '13px', borderRight: '1px solid var(--color-gray-100)' }}>
                        {balance ? (balance.annual?.used ?? 0) : '-'}
                      </td>
                      {/* 선사용 */}
                      <td style={{ padding: '8px 2px', textAlign: 'center', fontSize: '13px', borderRight: '1px solid var(--color-gray-100)', color: ((balance?.annual?.preUsed ?? 0) > 0) ? 'var(--color-orange-600)' : undefined }}>
                        {balance ? (balance.annual?.preUsed ?? 0) : '-'}
                      </td>
                      {/* 잔여 - V51: 마이너스 잔여일 때 빨간색 강조 */}
                      <td style={{ padding: '8px 2px', textAlign: 'center', fontSize: '13px', borderRight: '1px solid var(--color-gray-100)', color: ((balance?.annual?.remaining ?? 0) < 0) ? 'var(--color-red-600)' : undefined, fontWeight: ((balance?.annual?.remaining ?? 0) < 0) ? 600 : undefined }}>
                        {balance ? (balance.annual?.remaining ?? 0) : '-'}
                      </td>
                      {/* 적용시작 */}
                      {/* V62: CN 인턴은 연차 없음 → 적용기간 '-' 표시 */}
                      <td style={{ padding: '8px 2px', textAlign: 'center', fontSize: '13px', borderRight: '1px solid var(--color-gray-100)' }}>
                        {emp.companyCode === 'CN' && emp.currentEmploymentTypeCode === 'INTERN'
                          ? '-'
                          : (balance?.annual?.validFrom ?? balance?.annual?.periodStart ?? '-')}
                      </td>
                      {/* 적용종료 */}
                      <td style={{ padding: '8px 2px', textAlign: 'center', fontSize: '13px', borderRight: '1px solid var(--color-gray-100)' }}>
                        {emp.companyCode === 'CN' && emp.currentEmploymentTypeCode === 'INTERN'
                          ? '-'
                          : (balance?.annual?.validUntil ?? balance?.annual?.periodEnd ?? '-')}
                      </td>
                      {/* 1~12월 */}
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((m) => {
                        const val = monthlyUsage[m];
                        return (
                          <td key={`m-${m}`} style={{ padding: '8px 2px', textAlign: 'center', fontSize: '13px', borderRight: '1px solid var(--color-gray-100)' }}>
                            {val !== undefined ? (typeof val === 'number' ? parseFloat(val.toFixed(1)) : val) : '-'}
                          </td>
                        );
                      })}
                      {/* 퍼힐 1~4분기 */}
                      <td style={{ padding: '8px 2px', textAlign: 'center', fontSize: '12px', borderRight: '1px solid var(--color-gray-100)', color: q1 ? 'var(--color-green-600)' : 'var(--color-gray-400)' }}>
                        {q1 ? '사용' : '미사용'}
                      </td>
                      <td style={{ padding: '8px 2px', textAlign: 'center', fontSize: '12px', borderRight: '1px solid var(--color-gray-100)', color: q2 ? 'var(--color-green-600)' : 'var(--color-gray-400)' }}>
                        {q2 ? '사용' : '미사용'}
                      </td>
                      <td style={{ padding: '8px 2px', textAlign: 'center', fontSize: '12px', borderRight: '1px solid var(--color-gray-100)', color: q3 ? 'var(--color-green-600)' : 'var(--color-gray-400)' }}>
                        {q3 ? '사용' : '미사용'}
                      </td>
                      <td style={{ padding: '8px 2px', textAlign: 'center', fontSize: '12px', borderRight: '1px solid var(--color-gray-100)', color: q4 ? 'var(--color-green-600)' : 'var(--color-gray-400)' }}>
                        {q4 ? '사용' : '미사용'}
                      </td>
                      {/* V66: 대휴발생/대휴사용/대휴잔여 — balance.compensatory SSoT, 시간표기 nH (1일=8시간) */}
                      <td style={{ padding: '8px 2px', textAlign: 'center', fontSize: '13px', borderRight: '1px solid var(--color-gray-100)' }}>
                        {formatHours(balance?.compensatory?.granted)}
                      </td>
                      <td style={{ padding: '8px 2px', textAlign: 'center', fontSize: '13px', borderRight: '1px solid var(--color-gray-100)' }}>
                        {formatHours(balance?.compensatory?.used)}
                      </td>
                      <td style={{ padding: '8px 2px', textAlign: 'center', fontSize: '13px' }}>
                        {formatHours(balance?.compensatory?.remaining)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '40px', color: 'var(--color-gray-500)' }}>
            검색 결과가 없습니다.
          </div>
        )}
      </div>

      {/* V61: 대체휴무 지급 사이드 모달 */}
      {compOffTarget && (
        <div
          style={{
            position: 'fixed', top: 0, right: 0, bottom: 0,
            width: '400px', background: 'white',
            boxShadow: '-4px 0 20px rgba(0,0,0,0.15)',
            zIndex: 1000, display: 'flex', flexDirection: 'column',
          }}
        >
          {/* 헤더 */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', borderBottom: '1px solid var(--color-gray-200)' }}>
            <h3 style={{ fontSize: '16px', fontWeight: 'bold', margin: 0 }}>대체휴무 지급</h3>
            <button onClick={() => setCompOffTarget(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}>
              <X size={20} />
            </button>
          </div>

          {/* 본문 */}
          <div style={{ padding: '24px', flex: 1, overflowY: 'auto' }}>
            {/* 대상 직원 정보 */}
            <div style={{ marginBottom: '20px', padding: '12px', background: 'var(--color-gray-50)', borderRadius: '8px' }}>
              <p style={{ fontSize: '14px', fontWeight: '600', marginBottom: '4px' }}>{compOffTarget.nameKo}</p>
              <p style={{ fontSize: '12px', color: 'var(--color-gray-500)' }}>
                {compOffTarget.employeeNumber} · {compOffTarget.orgUnitName}
              </p>
              <p style={{ fontSize: '12px', color: 'var(--color-gray-500)', marginTop: '4px' }}>
                현재 대체휴무: 발생 {formatCompOffDaysHours(compOffTarget.balance?.compensatory?.granted ?? 0)} /
                사용 {formatCompOffDaysHours(compOffTarget.balance?.compensatory?.used ?? 0)} /
                잔여 {formatCompOffDaysHours(compOffTarget.balance?.compensatory?.remaining ?? 0)}
              </p>
            </div>

            {/* V78: 지급 단위 선택 */}
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>지급 단위</label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  type="button"
                  onClick={() => setCompOffUnit('days')}
                  style={{
                    flex: 1, padding: '8px 12px', fontSize: '13px', borderRadius: '6px',
                    border: `1px solid ${compOffUnit === 'days' ? 'var(--color-primary)' : 'var(--color-gray-300)'}`,
                    background: compOffUnit === 'days' ? 'var(--color-primary)' : 'white',
                    color: compOffUnit === 'days' ? 'white' : 'var(--color-gray-700)',
                    cursor: 'pointer', fontWeight: compOffUnit === 'days' ? 600 : 400,
                  }}
                >
                  일수 지급
                </button>
                <button
                  type="button"
                  onClick={() => setCompOffUnit('hours')}
                  style={{
                    flex: 1, padding: '8px 12px', fontSize: '13px', borderRadius: '6px',
                    border: `1px solid ${compOffUnit === 'hours' ? 'var(--color-primary)' : 'var(--color-gray-300)'}`,
                    background: compOffUnit === 'hours' ? 'var(--color-primary)' : 'white',
                    color: compOffUnit === 'hours' ? 'white' : 'var(--color-gray-700)',
                    cursor: 'pointer', fontWeight: compOffUnit === 'hours' ? 600 : 400,
                  }}
                >
                  시간 지급
                </button>
              </div>
            </div>

            {/* 지급 값 입력 (단위에 따라 전환) */}
            {compOffUnit === 'days' ? (
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>지급 일수</label>
                <input
                  type="number"
                  step="0.5"
                  min="0.5"
                  value={compOffDays}
                  onChange={(e) => setCompOffDays(e.target.value)}
                  placeholder="예: 0.5, 1, 1.5"
                  style={{
                    width: '100%', padding: '8px 12px', border: '1px solid var(--color-gray-300)',
                    borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box',
                  }}
                />
                <p style={{ fontSize: '11px', color: 'var(--color-gray-400)', marginTop: '4px' }}>
                  0.5일 단위 입력 (0.5, 1, 1.5 등) — 1일 = 8시간 기준
                </p>
              </div>
            ) : (
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>지급 시간</label>
                <input
                  type="number"
                  step="1"
                  min="1"
                  value={compOffHours}
                  onChange={(e) => setCompOffHours(e.target.value)}
                  placeholder="예: 1, 2, 4"
                  style={{
                    width: '100%', padding: '8px 12px', border: '1px solid var(--color-gray-300)',
                    borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box',
                  }}
                />
                <p style={{ fontSize: '11px', color: 'var(--color-gray-400)', marginTop: '4px' }}>
                  정수 시간 단위 (최소 1시간). 서버에서 직원 근로일정 기준으로 일수 환산됨.
                </p>
              </div>
            )}

            {/* 사유 */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>지급 사유</label>
              <textarea
                value={compOffReason}
                onChange={(e) => setCompOffReason(e.target.value)}
                placeholder="대체휴무 지급 사유를 입력하세요"
                rows={3}
                style={{
                  width: '100%', padding: '8px 12px', border: '1px solid var(--color-gray-300)',
                  borderRadius: '6px', fontSize: '14px', resize: 'vertical', boxSizing: 'border-box',
                }}
              />
            </div>

            {/* 에러 메시지 */}
            {compOffError && (
              <p style={{ color: 'var(--color-red-600)', fontSize: '13px', marginBottom: '12px' }}>{compOffError}</p>
            )}

            {/* V64: 지급 버튼을 입력 영역 바로 아래로 이동 */}
            <button
              onClick={handleGrantCompOff}
              disabled={compOffLoading}
              style={{
                width: '100%',
                padding: '10px 16px',
                background: compOffLoading ? 'var(--color-gray-400)' : 'var(--color-blue-600)',
                color: 'white', border: 'none', borderRadius: '6px',
                cursor: compOffLoading ? 'not-allowed' : 'pointer',
                fontSize: '14px', fontWeight: 600,
                marginBottom: '24px',
              }}
            >
              {compOffLoading ? '처리 중...' : '지급'}
            </button>

            {/* V64: 대체휴무 지급 히스토리 */}
            <div style={{ borderTop: '1px solid var(--color-gray-200)', paddingTop: '16px' }}>
              <h4 style={{ fontSize: '13px', fontWeight: 600, margin: '0 0 12px 0', color: 'var(--color-gray-700)' }}>
                대체휴무 지급 히스토리
              </h4>

              {historyLoading && (
                <p style={{ fontSize: '12px', color: 'var(--color-gray-500)', textAlign: 'center', padding: '16px 0' }}>
                  불러오는 중...
                </p>
              )}

              {!historyLoading && grantHistories.length === 0 && (
                <p style={{ fontSize: '12px', color: 'var(--color-gray-400)', textAlign: 'center', padding: '16px 0' }}>
                  지급 이력이 없습니다
                </p>
              )}

              {!historyLoading && grantHistories.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {grantHistories.map((h) => {
                    const grantedDate = new Date(h.grantedAt);
                    const dateStr = isNaN(grantedDate.getTime())
                      ? '-'
                      : `${grantedDate.getFullYear()}-${String(grantedDate.getMonth() + 1).padStart(2, '0')}-${String(grantedDate.getDate()).padStart(2, '0')} ${String(grantedDate.getHours()).padStart(2, '0')}:${String(grantedDate.getMinutes()).padStart(2, '0')}`;
                    const amountLabel = h.inputType === 'hours'
                      ? `${h.inputValue}시간`
                      : formatCompOffDaysHours(h.days);
                    return (
                      <div
                        key={h.id}
                        style={{
                          padding: '10px 12px',
                          background: h.status === 'cancelled' ? 'var(--color-gray-50)' : 'white',
                          border: '1px solid var(--color-gray-200)',
                          borderRadius: '6px',
                          fontSize: '12px',
                          opacity: h.status === 'cancelled' ? 0.6 : 1,
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                          <span style={{ fontWeight: 600, color: 'var(--color-gray-700)' }}>{dateStr}</span>
                          <span style={{
                            fontWeight: 600,
                            color: h.status === 'cancelled' ? 'var(--color-gray-400)' : 'var(--color-blue-600)',
                            textDecoration: h.status === 'cancelled' ? 'line-through' : 'none',
                          }}>
                            {amountLabel}
                          </span>
                        </div>
                        {h.reason && (
                          <div style={{ color: 'var(--color-gray-600)', marginBottom: '2px', wordBreak: 'break-word' }}>
                            {h.reason}
                          </div>
                        )}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div style={{ color: 'var(--color-gray-400)', fontSize: '11px' }}>
                            지급자: {h.grantedByName || h.grantedBy || '-'}
                            {h.status === 'cancelled' && <span style={{ marginLeft: '6px', color: 'var(--color-red-500)' }}>(취소됨)</span>}
                          </div>
                          {/* V70: 취소 버튼 (active 상태만) */}
                          {h.status === 'active' && (
                            <button
                              onClick={() => { setCancelTargetGrant(h); setCancelReason(''); setCancelError(null); }}
                              style={{
                                padding: '4px 10px',
                                fontSize: '11px',
                                border: '1px solid var(--color-red-500)',
                                background: 'white',
                                color: 'var(--color-red-500)',
                                borderRadius: '4px',
                                cursor: 'pointer',
                              }}
                            >
                              취소
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* V70: 대체휴무 지급 취소 확인 모달 */}
      {cancelTargetGrant && (
        <>
          <div
            onClick={() => !cancelLoading && setCancelTargetGrant(null)}
            style={{
              position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
              background: 'rgba(0,0,0,0.5)', zIndex: 1000,
            }}
          />
          <div style={{
            position: 'fixed',
            top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
            background: 'white', borderRadius: '8px', padding: '20px',
            width: '420px', maxWidth: '90vw',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)', zIndex: 1001,
          }}>
            <h3 style={{ fontSize: '15px', fontWeight: 600, margin: '0 0 12px 0' }}>대체휴무 지급 취소</h3>
            <div style={{ fontSize: '12px', color: 'var(--color-gray-700)', background: 'var(--color-gray-50)', padding: '10px', borderRadius: '6px', marginBottom: '12px' }}>
              <div style={{ marginBottom: '4px' }}>
                <strong>대상:</strong> {compOffTarget?.nameKo} ({compOffTarget?.employeeNumber})
              </div>
              <div style={{ marginBottom: '4px' }}>
                <strong>지급량:</strong> {cancelTargetGrant.inputType === 'hours'
                  ? `${cancelTargetGrant.inputValue}시간`
                  : formatCompOffDaysHours(cancelTargetGrant.days)}
              </div>
              <div style={{ marginBottom: '4px' }}>
                <strong>지급 사유:</strong> {cancelTargetGrant.reason || '-'}
              </div>
              <div>
                <strong>지급일:</strong> {(() => {
                  const d = new Date(cancelTargetGrant.grantedAt);
                  return isNaN(d.getTime()) ? '-' : `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
                })()}
              </div>
            </div>
            <label style={{ fontSize: '12px', fontWeight: 500, color: 'var(--color-gray-700)', display: 'block', marginBottom: '6px' }}>
              취소 사유 <span style={{ color: 'var(--color-red-500)' }}>*</span>
            </label>
            <textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              rows={3}
              style={{
                width: '100%', padding: '8px', fontSize: '13px',
                border: '1px solid var(--color-gray-300)', borderRadius: '4px',
                resize: 'vertical', boxSizing: 'border-box',
              }}
              placeholder="취소 사유를 입력하세요"
              disabled={cancelLoading}
            />
            {cancelError && (
              <div style={{ fontSize: '12px', color: 'var(--color-red-500)', marginTop: '8px' }}>{cancelError}</div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}>
              <button
                onClick={() => { if (!cancelLoading) { setCancelTargetGrant(null); setCancelReason(''); } }}
                disabled={cancelLoading}
                style={{
                  padding: '8px 14px', fontSize: '13px',
                  border: '1px solid var(--color-gray-300)',
                  background: 'white', color: 'var(--color-gray-700)',
                  borderRadius: '4px', cursor: cancelLoading ? 'not-allowed' : 'pointer',
                }}
              >
                닫기
              </button>
              <button
                onClick={handleConfirmCancelGrant}
                disabled={cancelLoading}
                style={{
                  padding: '8px 14px', fontSize: '13px',
                  border: '1px solid var(--color-red-500)',
                  background: 'var(--color-red-500)', color: 'white',
                  borderRadius: '4px', cursor: cancelLoading ? 'not-allowed' : 'pointer',
                  fontWeight: 500,
                }}
              >
                {cancelLoading ? '처리 중...' : '지급 취소'}
              </button>
            </div>
          </div>
        </>
      )}

      {/* V61: 모달 배경 오버레이 */}
      {compOffTarget && (
        <div
          onClick={() => setCompOffTarget(null)}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.3)', zIndex: 999,
          }}
        />
      )}
    </div>
  );
}
